import { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { Company, createCompany } from '../../services/coreCompaniesApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { formatCnpjMask, isValidCnpj, normalizeCnpj } from '../../utils/cnpjValidator';
import { formatPhoneMask } from '../../utils/formatPhoneMask';
import { ModalPortalShell } from '../ui/ModalPortalShell';
import { RequiredMark } from '../ui/RequiredMark';

type CompanyQuickCreateModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (company: Company) => void;
  description?: string;
};

export function CompanyQuickCreateModal({
  open,
  onClose,
  onCreated,
  description = 'Cadastro rápido para vincular à oportunidade. Você pode completar os dados depois.',
}: CompanyQuickCreateModalProps) {
  const [name, setName] = useState('');
  const [document, setDocument] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    if (!name.trim()) return;

    const documentValue = document.trim();
    if (documentValue && !isValidCnpj(documentValue)) {
      toast.error('CNPJ inválido. Informe um CNPJ numérico ou alfanumérico válido.');
      return;
    }

    setSaving(true);
    try {
      const company = await createCompany({
        name: name.trim(),
        document: documentValue ? normalizeCnpj(documentValue) : undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        isActive: true,
      });
      toast.success('Empresa cadastrada.');
      onCreated(company);
      setName('');
      setDocument('');
      setEmail('');
      setPhone('');
      onClose();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao cadastrar empresa.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortalShell
      open={open}
      panelClassName="erp-card max-w-md shadow-xl"
      overlayClassName="z-[60]"
    >
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="shrink-0 border-b border-secondary-200 px-5 py-4">
          <h3 className="erp-card-title">Nova empresa</h3>
          <p className="text-sm text-secondary-600">{description}</p>
        </div>

        <div className="erp-card-body erp-form min-h-0 flex-1 overflow-y-auto">
          <label className="erp-label">
            Nome <RequiredMark />
            <input
              className="erp-input"
              placeholder="Razão social ou nome fantasia"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </label>

          <div className="erp-form-grid">
            <label className="erp-label">
              CNPJ
              <input
                className="erp-input font-mono"
                value={document}
                onChange={(e) => setDocument(formatCnpjMask(e.target.value))}
                placeholder="00.000.000/0000-00"
                autoComplete="off"
                spellCheck={false}
                maxLength={18}
              />
            </label>
            <label className="erp-label">
              Telefone
              <input
                type="tel"
                inputMode="numeric"
                className="erp-input"
                placeholder="(11) 3333-4444"
                maxLength={15}
                value={phone}
                onChange={(e) => setPhone(formatPhoneMask(e.target.value))}
              />
            </label>
          </div>

          <label className="erp-label">
            E-mail
            <input
              type="email"
              className="erp-input"
              placeholder="contato@empresa.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
        </div>

        <div className="erp-btn-actions shrink-0 border-t border-secondary-200 bg-white px-5 py-4">
          <button type="button" className="erp-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="erp-btn-primary">
            {saving ? 'Salvando...' : 'Cadastrar'}
          </button>
        </div>
      </form>
    </ModalPortalShell>
  );
}
